// src/pages/Events.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./Events.css";
import { useConfirm } from "../components/ConfirmModal";
import { fmtKST, toMillisForSort } from "../utils/datetime";
import { maskPhone } from "../utils/phone";
import { getEvents, deleteEvents, restoreEvents, downloadFile } from "../utils/api";




/* === 페이지 내부 모달 === */
function Modal({
  open,
  title = "알림",
  message,
  loading = false,
  danger = false,
  closable = true,
  onClose,
}) {
  if (!open) return null;
  const tryClose = () => { if (closable && onClose) onClose(); };
  return (
    <div className="modal-backdrop" onClick={tryClose}>
      <div
        className={`modal-panel${danger ? " danger" : ""}${loading ? " loading" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head"><h3>{title}</h3></header>
        <div className="modal-body">
          {loading ? (
            <div className="loading-box">
              <div className="modal-spinner" />
              <p>서버에서 데이터를 불러오는 중입니다…</p>
            </div>
          ) : (message && <p>{message}</p>)}
        </div>
        <footer className="modal-foot">
          <button className="btn" onClick={tryClose}>닫기</button>
        </footer>
      </div>
    </div>
  );
}

// 화면이 "모바일 레이아웃(<=640px)"이면 true
function useIsMobile() {
  const get = () => {
    if (typeof window === "undefined") return false;
    // 폭 기반 미디어쿼리: 실제 레이아웃 브레이크포인트와 동일하게
    const mq = window.matchMedia?.("(max-width: 640px)");
    if (mq && typeof mq.matches === "boolean") return mq.matches;

    // matchMedia 미지원 대비
    const w = Math.min(window.innerWidth || 0, document.documentElement?.clientWidth || Infinity);
    return w <= 640;
  };

  const [isMobile, setIsMobile] = React.useState(get);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);

    // 최신/구형 브라우저 겸용
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    // 초기에 한 번 보정
    setIsMobile(mql.matches);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isMobile;
}



export default function Events() {
  const isMobile = useIsMobile();
  const { confirm: askConfirm, ConfirmDialog } = useConfirm();
  const abortRef = useRef(null);

  const POLL_MS = 0;

  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // 경고 말풍선
  const warnTimer = useRef(null);
  const [warningFor, setWarningFor] = useState(null);
  const popWarn = (which) => {
    setWarningFor(which);
    clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => setWarningFor(null), 1800);
  };
  useEffect(() => () => clearTimeout(warnTimer.current), []);

  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [modal, setModal] = useState({
    open: false, title: "", message: "", loading: false, danger: false, closable: true
  });

  const [authExpired, setAuthExpired] = useState(false); //  세션 만료 플래그
 

  /* ===== 검색 상태 ===== */
  const EMPTY_Q = useMemo(
    () => ({ name:"", phone:"", address:"", symptom:"", occurredAt:"", handledAt:"", action:"", ackedBy:"" }),
    []
  );
  const [draft, setDraft] = useState(() => ({ ...EMPTY_Q }));
  const [q, setQ]       = useState(() => ({ ...EMPTY_Q }));
  const isFilterActive = useMemo(
    () => Object.values(q).some(v => String(v ?? "").trim() !== ""),
    [q]
  );

  const normLoose = (s = "") => String(s).toLowerCase().replace(/\s+/g, "");
  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const sanitizePhone = (s = "") => String(s).replace(/[^0-9-]/g, "");


  const applySearch = () => { setQ(draft); setPage(1); };
  const clearSearch = () => { setDraft({ ...EMPTY_Q }); };
  const showAll = useCallback(() => { setDraft({ ...EMPTY_Q }); setQ({ ...EMPTY_Q }); setPage(1); }, [EMPTY_Q]);

  const hasMeaningfulQuery = useCallback((d) => {
    const anyText = [d.name, d.address, d.symptom, d.occurredAt, d.handledAt, d.action, d.ackedBy].some(v => normLoose(v).length > 0);
    const dashInText = [d.name, d.address, d.symptom, d.occurredAt, d.action, d.ackedBy].some(v => String(v).trim() === "-");
    const phoneMeaningful = onlyDigits(d.phone).length > 0 || String(d.phone).trim() === "-";
    return anyText || dashInText || phoneMeaningful;
  }, []);
    const onSubmitSearch = (e) => {
      e.preventDefault();
      if (hasMeaningfulQuery(draft)) {
        applySearch();
      } else {
        // 모든 입력이 비어있으면 '전체 보기' 실행
        showAll();
      }
  };


  /* ===== 페이지네이션 상태 ===== */
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // helpers
  const isHandledMissing = (h) => !h || (typeof h === "string" && h.replace(/\s/g, "").includes("미처리"));
  const isEmpty = (v) => v == null || (typeof v === "string" && v.trim() === "");
  const isDashOrEmpty = (v) => v == null || ["", "-"].includes(String(v).trim());

  const actionLabel = useCallback((action, ackedBy, handledAt) => {
    const isBlankOrDash = (v) => v == null || String(v).trim() === "" || String(v).trim() === "-";
    const code = typeof action === "string" ? action.trim().toUpperCase() : "";
    const handled = !isHandledMissing(handledAt);
    if (code === "START") return "직원 조치";
    if (!handled) return "-";
    if (code === "REPORT" && !isBlankOrDash(ackedBy)) return "119신고(수동)";
    if (isBlankOrDash(code) && isBlankOrDash(ackedBy)) return "119신고(자동)";
    return "-";
  }, []);

  /* === 토큰/매칭 유틸 (전 필드 공통) === */
  const tokenize = (q = "") =>
    (String(q).match(/[0-9]+|[a-zA-Z]+|[가-힣]+/g) || []).map(t => t.trim()).filter(Boolean);

  const containsSubsequence = (hay = "", needle = "") => {
    if (!needle) return true;
    let i = 0;
    const H = String(hay);
    const N = String(needle);
    for (let k = 0; k < H.length && i < N.length; k++) {
      if (H[k] === N[i]) i++;
    }
    return i === N.length;
  };

  const tokenMatchInText = useCallback((textRaw, query) => {
    const tokens = tokenize(query);
    if (!tokens.length) return true;

    const text = String(textRaw ?? "");
    const textNorm = text.toLowerCase().replace(/\s+/g, "");
    const textDigits = text.replace(/\D/g, "");

    return tokens.every((t0) => {
      const t = t0.toLowerCase();

      if (/^a\.?m\.?$/.test(t)) return /오전|\bam\b/i.test(text);
      if (/^p\.?m\.?$/.test(t)) return /오후|\bpm\b/i.test(text);

      if (/^\d+$/.test(t)) return containsSubsequence(textDigits, t);

      const tNorm = t.replace(/\s+/g, "");
      return containsSubsequence(textNorm, tNorm);
    });
  }, []); // 순수 유틸

  /* ===== 매칭 함수들 ===== */
  const matchesDashOrIncludes = useCallback((value, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    if (q === "-") return isDashOrEmpty(value);
    return tokenMatchInText(value ?? "", q);
  }, [tokenMatchInText]);

  const matchesPhone = useCallback((phoneValue, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    if (q === "-") return isDashOrEmpty(phoneValue);
    return tokenMatchInText(phoneValue ?? "", q);
  }, [tokenMatchInText]);

  const matchesOccurredAt = useCallback((value, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    if (q === "-") return !value;
    const text = fmtKST(value) || "-";
    return tokenMatchInText(text, q);
  }, [tokenMatchInText]);

  const matchesHandledAt = useCallback((handledAtValue, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    const text = handledAtValue ? (fmtKST(handledAtValue) || "-") : "미처리";
    return tokenMatchInText(text, q);
  }, [tokenMatchInText]);

  const matchesAction = useCallback((action, ackedBy, handledAt, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    const label = actionLabel(action, ackedBy, handledAt);
    if (q === "-") return String(label).trim() === "-";
    return tokenMatchInText(label, q);
  }, [actionLabel, tokenMatchInText]);

  /* ===== 검색 입력 좌/우 화살표 ===== */
  const searchRefs = useRef([]);
  const handleArrowNav = (e, idx) => {
    const el = e.target;

    // ── 모바일: ↑/↓ 로 위/아래 칸 이동 ───────────────────────────
    if (isMobile) {
      if (e.key === "ArrowUp" && idx > 0) {
        e.preventDefault();
        const prev = searchRefs.current[idx - 1];
        if (prev) {
          prev.focus();
          const len = prev.value.length;
          requestAnimationFrame(() => prev.setSelectionRange?.(len, len));
        }
      }
      if (e.key === "ArrowDown" && idx < searchRefs.current.length - 1) {
        e.preventDefault();
        const next = searchRefs.current[idx + 1];
        if (next) {
          next.focus();
          requestAnimationFrame(() => next.setSelectionRange?.(0, 0));
        }
      }
      return; // 모바일은 여기서 끝
    }

    // ── 데스크톱: 기존 ←/→ (커서가 맨앞/맨끝일 때만 이동) ───────────
    const posStart = el.selectionStart ?? 0;
    const posEnd = el.selectionEnd ?? 0;
    const atStart = posStart === 0 && posEnd === 0;
    const atEnd = posStart === el.value.length && posEnd === el.value.length;

    if (e.key === "ArrowLeft" && atStart && idx > 0) {
      e.preventDefault();
      const prev = searchRefs.current[idx - 1];
      if (prev) {
        prev.focus();
        const len = prev.value.length;
        requestAnimationFrame(() => prev.setSelectionRange?.(len, len));
      }
    }
    if (e.key === "ArrowRight" && atEnd && idx < searchRefs.current.length - 1) {
      e.preventDefault();
      const next = searchRefs.current[idx + 1];
      if (next) {
        next.focus();
        requestAnimationFrame(() => next.setSelectionRange?.(0, 0));
      }
    }
  };


  /* ===== 목록 로드 ===== */
  const fetchData = useCallback(async (deleted = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      setLastError(null);
      let list = await getEvents(deleted, { signal: controller.signal });
      if (!Array.isArray(list)) list = [];
      const sorted = [...list].sort((a, b) => {
        const getT = (r) => toMillisForSort(r.occurredAt ?? r.createdAt ?? null);
        return getT(b) - getT(a);
      });
      setData(sorted);
      setPage(1);
      setAuthExpired(false); //  성공 시 만료 해제
    }  catch (e) {
  if (e.name !== "AbortError") {
    console.error("[Events] load error", e);
    setLastError(e);
    setData([]);

    // 세션 만료 여부 추정(Cloudflare Access CORS/리다이렉트 포함)
    const msg = String(e?.message || "");
    const status = e?.status || e?.response?.status;
    const code = e?.code || e?.response?.data?.code;
    const looksLikeAccessRedirect =
      msg.includes("Failed to fetch") ||
      msg.includes("TypeError") ||
      msg.toLowerCase().includes("networkerror");

    if (
      code === "AUTH_EXPIRED" ||
      [401, 403, 419, 440].includes(Number(status)) ||
      looksLikeAccessRedirect
    ) {
      setAuthExpired(true);
    } else {
      setAuthExpired(false);
    }
  }
}
 finally {
      setLoading(false);
    }
 }, []);

  useEffect(() => { fetchData(); return () => abortRef.current?.abort(); }, [fetchData]);
  useEffect(() => {
    const onReset = () => showAll();
    window.addEventListener("reset-events-search", onReset);
    return () => window.removeEventListener("reset-events-search", onReset);
  }, [showAll]);

  useEffect(() => {
    if (!modal.open || !modal.loading) return;
    if (loading) return;
    if (lastError) {
      setModal({ open: true, title: "다운로드 불가", message: "서버 오류로 목록을 불러오지 못했습니다.", loading: false, danger: true, closable: true });
    } else if (!data.length) {
      setModal({ open: true, title: "다운로드 불가", message: "표시할 데이터가 없어 다운로드할 수 없습니다.", loading: false, danger: true, closable: true });
    } else {
      setModal({ open: true, title: "불러오기 완료", message: "데이터 불러오기 완료했습니다. 다시 ‘엑셀 다운로드’ 버튼을 눌러주세요.", loading: false, danger: false, closable: true });
    }
  },[loading, modal.open, modal.loading, lastError, data.length]);

  // 필터 변경/데이터 변경 시 현재 page 유효성 보정
  useEffect(() => {
    const filteredX = data.filter(r => {
      if (q.name       && !matchesDashOrIncludes(r.name, q.name)) return false;
      if (q.phone      && !matchesPhone(r.phone, q.phone)) return false;
      if (q.address    && !matchesDashOrIncludes(r.address, q.address)) return false;
      if (q.symptom    && !matchesDashOrIncludes(r.symptom, q.symptom)) return false;
      if (q.occurredAt && !matchesOccurredAt(r.occurredAt, q.occurredAt)) return false;
      if (q.handledAt  && !matchesHandledAt(r.handledAt, q.handledAt)) return false;
      if (q.action     && !matchesAction(r.action, r.ackedBy, r.handledAt, q.action)) return false;
      if (q.ackedBy    && !matchesDashOrIncludes(r.ackedBy, q.ackedBy)) return false;
      return true;
    });
    const totalPagesX = Math.max(1, Math.ceil(filteredX.length / PAGE_SIZE));

    setPage(prev => (prev > totalPagesX ? totalPagesX : prev));
  }, [
    data,
    q,
    matchesHandledAt,
    matchesDashOrIncludes,
    matchesPhone,
    matchesOccurredAt,
    matchesAction,
    PAGE_SIZE
  ]);

  useEffect(() => {
    if (!POLL_MS) return;
    const t = setInterval(() => fetchData(showDeleted), POLL_MS);
    return () => clearInterval(t);
  }, [showDeleted, POLL_MS, fetchData]);

  /* ===== 체크박스/삭제/복원 ===== */
  const handleCheckbox = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 삭제(휴지통으로)
  const handleDelete = async () => {
    if (selectedRows.length === 0) { popWarn("delete"); return; }
    const ok = await askConfirm({ title: "삭제 확인", message: `선택한 이벤트 ${selectedRows.length}건을 삭제할까요?`, danger: true });
    if (!ok) return;
    try {
      await deleteEvents(selectedRows);               // ← utils 사용
      setData(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error("삭제 실패:", e);
      alert("삭제에 실패했습니다.");
    }
  };

  // 복원
  const handleRestore = async () => {
    if (selectedRows.length === 0) { popWarn("restore"); return; }
    const ok = await askConfirm({ title: "복원 확인", message: `선택한 이벤트 ${selectedRows.length}건을 복원할까요?` });
    if (!ok) return;
    try {
      await restoreEvents(selectedRows);              // ← utils 사용
      setData(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error("복원 실패:", e);
      alert("복원에 실패했습니다.");
    }
  };
  

  const handleHideLocal = async () => {
    if (selectedRows.length === 0) { popWarn("purge"); return; }
    const ok = await askConfirm({
      title: "목록에서 없애기",
      message: (<>선택한 {selectedRows.length}건을 이벤트 목록에서<br className="br-480" /><span style={{whiteSpace:"nowrap"}}>없앨까요? (DB에는 남습니다)</span></>)
    });
    if (!ok) return;
    setData((prev) => prev.filter((r) => !selectedRows.includes(r.id)));
    setSelectedRows([]);
  };

  const handleToggleView = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    setSelectedRows([]);
    setPage(1);
    fetchData(next);
  };

  /* ===== CSV 유틸 ===== */
  const csvEscape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const toExcelText = (v) => {
    const s = (v == null || (typeof v === "string" && v.trim() === "")) ? "-" : String(v);
    return `="${s.replace(/"/g, '""')}"`;
  };

      // 기존 kstFilenameTimestamp() 자리에 그대로 교체
    const kstFilenameTimestamp = () => {
      const parts = new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",       // 12시간제
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }).formatToParts(new Date());

      const get = (t) => parts.find(p => p.type === t)?.value;
      const year = get("year");
      const month = get("month");
      const day = get("day");
      const dp = get("dayPeriod") || "";   // "오전" / "오후"
      const hour = get("hour");            // 1~12
      const minute = get("minute")?.padStart(2, "0");
      const second = get("second")?.padStart(2, "0");

      // 파일명에 쓰기 좋게 공백 대신 언더스코어 사용
      return `${year}-${month}-${day}_${dp}_${hour}시 ${minute}분 ${second}초`;
    };

  const downloadCSV = (filename, headers, rows) => {
    const lines = [];
    lines.push(headers.map(csvEscape).join(","));
    rows.forEach((r) => lines.push(r.map(csvEscape).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
  };

  /* ===== 필터/페이지 ===== */
  const baseRows = data;

  const filtered = baseRows.filter(r => {
    if (q.name       && !matchesDashOrIncludes(r.name, q.name)) return false;
    if (q.phone      && !matchesPhone(r.phone, q.phone)) return false;
    if (q.address    && !matchesDashOrIncludes(r.address, q.address)) return false;
    if (q.symptom    && !matchesDashOrIncludes(r.symptom, q.symptom)) return false;
    if (q.occurredAt && !matchesOccurredAt(r.occurredAt, q.occurredAt)) return false;
    if (q.handledAt  && !matchesHandledAt(r.handledAt, q.handledAt)) return false;
    if (q.action     && !matchesAction(r.action, r.ackedBy, r.handledAt, q.action)) return false;
    if (q.ackedBy    && !matchesDashOrIncludes(r.ackedBy, q.ackedBy)) return false;
    return true;
  });

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  /* ===== 다운로드 버튼 ===== */
  const handleDownload = () => {
    if (loading) {
      setModal({ open: true, title: "불러오는 중…", message: "서버에서 데이터를 불러오는 중입니다. 완료 후 안내를 확인해 주세요.", loading: true, danger: false, closable: true });
      return;
    }
    if (lastError) {
      setModal({ open: true, title: "다운로드 불가", message: "서버 오류로 목록을 불러오지 못했습니다.", loading: false, danger: true, closable: true });
      return;
    }
    if (!data.length) {
      setModal({ open: true, title: "다운로드 불가", message: "표시할 데이터가 없어 다운로드할 수 없습니다.", loading: false, danger: true, closable: true });
      return;
    }

    const headers = ["번호","이름","전화번호","주소","증상","발생 일시","처리 일시","조치 방법","조치자"];

    if (isFilterActive) {
      const rows = filtered.map((row, idx) => [
        toExcelText(idx + 1),
        toExcelText(row.name ?? "-"),
        toExcelText(maskPhone(row.phone)),
        toExcelText(row.address ?? "-"),
        toExcelText(row.symptom ?? "-"),
        toExcelText(fmtKST(row.occurredAt) || "-"),
        toExcelText(row.handledAt ? fmtKST(row.handledAt) : "미처리"),
        toExcelText(actionLabel(row.action, row.ackedBy, row.handledAt)),
        toExcelText(isDashOrEmpty(row.ackedBy) ? "-" : row.ackedBy),
      ]);
      const fname = `이벤트_${showDeleted ? "휴지통_" : ""}검색결과_${kstFilenameTimestamp()}.csv`;
      downloadCSV(fname, headers, rows);
      return;
    }

    if (showDeleted) {
      const rows = baseRows.map((row, idx) => [
        toExcelText(idx + 1),
        toExcelText(row.name ?? "-"),
        toExcelText(maskPhone(row.phone)),
        toExcelText(row.address ?? "-"),
        toExcelText(row.symptom ?? "-"),
        toExcelText(fmtKST(row.occurredAt) || "-"),
        toExcelText(row.handledAt ? fmtKST(row.handledAt) : "미처리"),
        toExcelText(actionLabel(row.action, row.ackedBy, row.handledAt)),
        toExcelText(isDashOrEmpty(row.ackedBy) ? "-" : row.ackedBy),
      ]);
      const fname = `이벤트_휴지통_전체_${kstFilenameTimestamp()}.csv`;
      downloadCSV(fname, headers, rows);
    } else {
      const fname = `이벤트_전체_${kstFilenameTimestamp()}.xlsx`;
      downloadFile(`/api/events/download`, fname).catch(() => {
        setModal({
          open: true,
          title: "다운로드 실패",
          message: "파일을 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.",
          loading: false,
          danger: true,
          closable: true
        });
      });
    }

  };

  /* ===== 렌더 ===== */
  const Pagination = ({ page, totalPages, total, setPage }) => {
    const totalPagesSafe = Math.max(1, totalPages);

    const [jump, setJump] = useState(String(page));
    useEffect(() => {
      const next = String(page);
      setJump(prev => (prev === next ? prev : next));
    }, [page]);

    const clamp = (n) =>
      Math.min(totalPagesSafe, Math.max(1, Number.isFinite(n) ? Math.trunc(n) : 1));

    const goFirst = () => setPage(1);
    const goPrev  = () => setPage((p) => Math.max(1, p - 1));
    const goNext  = () => setPage((p) => Math.min(totalPagesSafe, p + 1));
    const goLast  = () => setPage(totalPagesSafe);

    const tryJump = () => {
      const n = Number(jump);
      if (!Number.isFinite(n)) return;
      setPage(clamp(n));
    };
    const onJumpKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        tryJump();
      }
    };

    const disablePrev = page <= 1;
    const disableNext = page >= totalPagesSafe;

    if (!total) return null;

    return (
      <div className="pagination" role="navigation" aria-label="페이지네이션">
        <div className="pager">
          <button
            className="pg-btn pg-first"
            title="맨 처음"
            aria-label="맨 처음 페이지"
            onClick={goFirst}
            disabled={disablePrev}
          />
          <button className="pg-btn" title="이전" aria-label="이전 페이지"
                  onClick={goPrev} disabled={disablePrev}>◀</button>

          <button className="pager-current" disabled aria-current="page">
            {page}
          </button>

          <button className="pg-btn" title="다음" aria-label="다음 페이지"
                  onClick={goNext} disabled={disableNext}>▶</button>
          <button
            className="pg-btn pg-last"
            title="맨 끝"
            aria-label="맨 끝 페이지"
            onClick={goLast}
            disabled={disableNext}
          />

          <div className="pg-jump">
            <input
              className="pg-input"
              type="number"
              min={1}
              max={totalPagesSafe}
              value={jump}
              onChange={(e) => setJump(e.target.value)}
              onKeyDown={onJumpKey}
              aria-label="이동할 페이지 입력"
            />
            <button className="pg-go" onClick={tryJump}>이동</button>
          </div>
        </div>

        <div className="page-info">
          페이지 {page}/{totalPagesSafe} · 총 {total.toLocaleString()}건
        </div>
      </div>
    );
  };

  return (
    <div className="admin-container events-page">
      <h2 className="admin-title">이벤트 목록</h2>

      <div className="delete-button-container">
        <div className="button-row">
          <button className="toggle-button" onClick={handleToggleView}>
            {showDeleted ? <>유효한 이벤트<br className="mobile-only-br" /> 보기</> : "삭제된 이벤트 보기"}
          </button>

          {showDeleted ? (
            <>
              <span className="btn-wrap">
                <button className="restore-button ev-restore" onClick={handleRestore}>복원</button>
                {warningFor === "restore" && <div className="warning-tooltip">복원할 행을 체크하세요</div>}
              </span>

              <span className="btn-wrap">
                <button className="purge-button" onClick={handleHideLocal}>
                  이벤트 목록에서<br className="mobile-only-br" /> 없애기
                </button>
                {warningFor === "purge" && <div className="warning-tooltip"> 없애고 싶은 행을 체크하세요</div>}
              </span>
            </>
          ) : (
            <span className="btn-wrap">
              <button className="delete-button" onClick={handleDelete}>삭제</button>
              {warningFor === "delete" && <div className="warning-tooltip">삭제할 행을 체크하세요</div>}
            </span>
          )}
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th></th><th>번호</th><th>이름</th><th>전화번호</th><th>주소</th>
            <th>증상</th><th>발생 일시</th><th>처리 일시</th><th>조치 방법</th><th>조치자</th>
          </tr>
        </thead>
        <tbody className={showDeleted ? "gray-out-rows" : ""}>
          {loading ? (
            <tr><td colSpan="10" className="error-message">불러오는 중…</td></tr>
          ) : pageRows.length ? (
              pageRows.map((row, idx) => {
              const pending = isHandledMissing(row.handledAt) || actionLabel(row.action, row.ackedBy, row.handledAt) === "-";
              return (
                <tr key={row.id} className={pending ? "pending-row" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={() => handleCheckbox(row.id)}
                    />
                  </td>
                  <td className="number-cell">{startIdx + idx + 1}</td>
                  <td>{isEmpty(row.name) ? "-" : row.name}</td>
                  <td>{isEmpty(row.phone) ? "-" : maskPhone(row.phone)}</td>
                  <td>{isEmpty(row.address) ? "-" : row.address}</td>
                  <td>{isEmpty(row.symptom) ? "-" : row.symptom}</td>
                  <td>{fmtKST(row.occurredAt)}</td>
                  <td>{row.handledAt ? fmtKST(row.handledAt) : <span className="text-michori">미처리</span>}</td>
                  <td>{actionLabel(row.action, row.ackedBy, row.handledAt)}</td>
                  <td>{isDashOrEmpty(row.ackedBy) ? "-" : row.ackedBy}</td>
                </tr>
              );
            })
          ) : (
            <tr><td colSpan="10" className="error-message">
              {authExpired
                ? "세션이 만료되었습니다. 새로고침(F5) 후 다시 로그인해 주세요."
                : (baseRows.length ? "검색 조건에 맞는 이벤트가 없습니다." : "표시할 데이터가 없습니다.")}
            </td></tr>
          )}
        </tbody>
      </table>

      <div className={`event-cards ${showDeleted ? "cards-deleted" : ""}`}>
        {loading ? (
          <div className="card-empty">불러오는 중…</div>
        ) : pageRows.length ? (
          pageRows.map((row, idx) => {
            const pending = isHandledMissing(row.handledAt) || actionLabel(row.action, row.ackedBy, row.handledAt) === "-";
            return (
              <article key={row.id} className={`event-card ${pending ? "pending-card" : ""}`}>
                <header className="card-top">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleCheckbox(row.id)}
                  />
                  <span className="no">번호 {startIdx + idx + 1}</span>
                </header>
                <dl className="ev-kv">
                  <div><dt>이름</dt><dd>{isEmpty(row.name) ? "-" : row.name}</dd></div>
                  <div><dt>전화번호</dt><dd className="phone">{isEmpty(row.phone) ? "-" : maskPhone(row.phone)}</dd></div>
                  <div><dt>주소</dt><dd className="multiline">{isEmpty(row.address) ? "-" : row.address}</dd></div>
                  <div><dt>증상</dt><dd className="multiline">{isEmpty(row.symptom) ? "-" : row.symptom}</dd></div>
                  <div><dt>발생</dt><dd>{row.occurredAt ? fmtKST(row.occurredAt) : "-"}</dd></div>
                  <div><dt>처리</dt><dd>{row.handledAt ? fmtKST(row.handledAt) : <span className="text-michori">미처리</span>}</dd></div>
                  <div><dt>조치</dt><dd className="multiline">{actionLabel(row.action, row.ackedBy, row.handledAt)}</dd></div>
                  <div><dt>조치자</dt><dd>{isDashOrEmpty(row.ackedBy) ? "-" : row.ackedBy}</dd></div>
                </dl>
              </article>
            );
          })
        ) : (
          <div className="card-empty">
            {authExpired
              ? "세션이 만료되었습니다. 새로고침(F5) 후 다시 로그인해 주세요."
              : (baseRows.length ? "검색 조건에 맞는 이벤트가 없습니다." : "표시할 데이터가 없습니다.")}
          </div>
        )}
      </div>

      <div className="bottom-row">
        <button className="download-button" onClick={handleDownload}>엑셀 다운로드</button>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} />
      </div>

      <div className="search-card wide">
        <form className="search-form" onSubmit={onSubmitSearch}>
          <input placeholder="이름"       value={draft.name}       onChange={e=>setDraft({...draft, name:e.target.value})}
                 ref={(el)=>searchRefs.current[0]=el} onKeyDown={(e)=>handleArrowNav(e,0)} />
          <input
            placeholder="전화번호"
            value={draft.phone}
            onChange={(e)=>setDraft({...draft, phone: sanitizePhone(e.target.value)})}
            ref={(el)=>searchRefs.current[1]=el}
            onKeyDown={(e)=>handleArrowNav(e,1)}
          />
          <input placeholder="주소"       value={draft.address}    onChange={e=>setDraft({...draft, address:e.target.value})}
                 ref={(el)=>searchRefs.current[2]=el} onKeyDown={(e)=>handleArrowNav(e,2)} />
          <input placeholder="증상"       value={draft.symptom}    onChange={e=>setDraft({...draft, symptom:e.target.value})}
                 ref={(el)=>searchRefs.current[3]=el} onKeyDown={(e)=>handleArrowNav(e,3)} />
          <input placeholder="발생 일시"   value={draft.occurredAt} onChange={e=>setDraft({...draft, occurredAt:e.target.value})}
                 ref={(el)=>searchRefs.current[4]=el} onKeyDown={(e)=>handleArrowNav(e,4)} />
          <input placeholder="처리 일시"   value={draft.handledAt}  onChange={e=>setDraft({...draft, handledAt:e.target.value})}
                 ref={(el)=>searchRefs.current[5]=el} onKeyDown={(e)=>handleArrowNav(e,5)} />
          <input placeholder="조치 방법"   value={draft.action}     onChange={e=>setDraft({...draft, action:e.target.value})}
                 ref={(el)=>searchRefs.current[6]=el} onKeyDown={(e)=>handleArrowNav(e,6)} />
          <input placeholder="조치자"     value={draft.ackedBy}    onChange={e=>setDraft({...draft, ackedBy:e.target.value})}
                 ref={(el)=>searchRefs.current[7]=el} onKeyDown={(e)=>handleArrowNav(e,7)} />
          <button type="submit" className="btn-search">검색</button>
          <button type="button" className="btn-reset" onClick={clearSearch}>취소</button>
          <button type="button" className="btn-showall" onClick={showAll} aria-label="전체 보기" title="전체 보기" />
        </form>
      </div>

      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        loading={modal.loading}
        danger={modal.danger}
        closable={modal.closable}
        onClose={() => setModal({ open: false, title: "", message: "", loading: false, danger: false, closable: true })}
      />
      {ConfirmDialog}
    </div>
  );
}